import VerificationEmailActivity, {
  EmailActivityPage,
} from "./VerificationEmailActivity";
import "./AddressVerificationReviews.css";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactModal from "react-modal";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";

type ReviewStatus = "pending_manual_review" | "verified" | "rejected";
type ReviewTab =
  | ReviewStatus
  | "needs_review"
  | "identity_verified"
  | "address_verified"
  | "all"
  | "automatically_approved"
  | "automatically_rejected";

type CandidateSelection = {
  candidateCount: number;
  matchingCandidateCount: number;
  reason: string;
  referenceCandidateIndex?: number;
  candidates?: Array<{
    index: number;
    placeId: string | null;
    precision: string | null;
    partialMatch: boolean;
    checks: Record<string, boolean | null>;
    reason: string;
  }>;
};
const candidateReasons: Record<string, string> = {
  address_components_match: "One property is supported by the bill address",
  address_components_mismatch:
    "No property sufficiently matches the bill address",
  ambiguous_address:
    "Multiple properties match; the bill location remains ambiguous",
  property_type_not_supported: "Result is not a supported property address",
  components_missing: "Structured address components are missing",
  country_not_matched: "Nigerian country information was not matched",
  bill_address_missing: "Bill address is missing",
  property_not_matched: "House, street or named property was not matched",
  locality_not_matched: "Locality was not matched",
  bill_components_unmatched: "Some bill address details remain unmatched",
  imprecise_location: "Address matches, but location precision is insufficient",
  selected: "Selected for the distance check",
  duplicate_candidate: "Duplicate of the selected property",
};
const candidateChecks: Record<string, string> = {
  propertyType: "Property address type",
  structuredComponents: "Structured components available",
  country: "Country",
  property: "House / street / property",
  locality: "Locality",
  remainingBillComponents: "Remaining bill details",
};

type AddressReview = {
  id: number;
  reviewRevision: string;
  canDecideAddress?: boolean;
  approvalSource?: "manual" | "automatic" | "unknown" | null;
  status: ReviewStatus | "pending" | null;
  submittedAddress?: string | null;
  submittedAt?: string;
  updatedAt?: string;
  user: {
    id: number;
    userName: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    level: number;
  } | null;
  document: {
    type?: string;
    ownerName?: string;
    address?: string;
    emissionDate?: string;
    imageUrl?: string;
    stepStatus?: number;
    error?: { code?: string; message?: string } | null;
  };
  identity: {
    status?: string | null;
    verificationId?: string | null;
    reviewRevision?: string | null;
    ninImageUrl?: string | null;
    bvnImageUrl?: string | null;
    selfieImageUrl?: string | null;
    dashboardUrl?: string | null;
    providerRejectionReason?: string | null;
    evidenceUnavailable?: boolean;
    canDecide?: boolean;
    canApprove?: boolean;
    approvalSource?: "manual" | "automatic" | "unknown" | null;
    approvalBlockedReason?: string | null;
    decision?: {
      status: string;
      reason: string;
      actorUserName: string;
      decidedAt: string;
    } | null;
    failureReason?: string | null;
    ninStatus?: string | null;
    bvnStatus?: string | null;
    nin?: Record<string, any> | null;
    bvn?: Record<string, any> | null;
    ninNumber?: string | null;
    bvnNumber?: string | null;
  };
  verification: {
    id?: string;
    status?: string;
    identityStatus?: string;
    dashboardUrl?: string;
    checks?: Record<string, boolean> | null;
    rejectionReason?: string | null;
  };
  decision?: {
    status?: string;
    reason?: string;
    adminUserName?: string;
    actorType?: "admin" | "support";
    actorUserName?: string;
    decidedAt?: string;
  } | null;
  location?: {
    latitude: number;
    longitude: number;
    accuracy: number;
    capturedAt: string;
    confirmedAt: string;
    resolvedAddress?: string | null;
    geocodedAt?: string | null;
    addressSource?:
      | "google_reverse_geocoding"
      | "google_places_nearby_agreement"
      | null;
  } | null;
  locationComparison?: {
    reason: string;
    distanceMeters: number | null;
    maxDistanceMeters: number;
    maxAccuracyMeters?: number;
    precision: string | null;
    checkedAt: string;
    billPlaceId?: string | null;
    billLocationAccepted?: boolean;
    candidateSelection?: CandidateSelection;
    textMatch?: {
      matched: boolean;
      reason: string;
      billStates: string[];
      liveStates: string[];
    };
  } | null;
};

const PAGE_LIMIT = 20;

const tabs: Array<{ value: ReviewStatus; label: string }> = [
  { value: "pending_manual_review", label: "Needs review" },
  { value: "verified", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];
const adminTabs: Array<{ value: ReviewTab; label: string }> = [
  ...tabs,
  { value: "automatically_approved", label: "Automatically approved" },
  { value: "automatically_rejected", label: "Automatically rejected" },
];
const kycTabs: Array<{ value: ReviewTab; label: string }> = [
  { value: "needs_review", label: "Needs review" },
  { value: "identity_verified", label: "Identity Verified" },
  { value: "address_verified", label: "Address Verified" },
  { value: "verified", label: "Verified" },
];

const statusStyle: Record<ReviewStatus, string> = {
  pending_manual_review: "bg-amber-100 text-amber-800",
  verified: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
};

const statusLabel: Record<ReviewTab, string> = {
  all: "All",
  needs_review: "Needs review",
  identity_verified: "Identity Verified",
  address_verified: "Address Verified",
  pending_manual_review: "Needs review",
  verified: "Approved",
  rejected: "Rejected",
  automatically_approved: "Automatically approved",
  automatically_rejected: "Automatically rejected",
};

const verificationLabel = (status?: string | null) => {
  switch (status) {
    case "verified":
      return "Verified";
    case "pending":
      return "Pending";
    case "pending_manual_review":
      return "Needs review";
    case "rejected":
      return "Rejected";
    case "expired":
      return "Expired";
    case null:
      return "Not started";
    default:
      return "Not available";
  }
};

const VerificationBadge = ({
  label,
  status,
}: {
  label: string;
  status?: string | null;
}) => (
  <span
    className={`inline-block rounded-lg px-2.5 py-1 text-xs font-semibold ${
      statusStyle[status as ReviewStatus] ||
      (status === "pending"
        ? "bg-amber-100 text-amber-800"
        : "bg-gray-100 text-gray-600")
    }`}
  >
    {label}: {verificationLabel(status)}
  </span>
);

const displayName = (review: AddressReview) =>
  [review.user?.firstName, review.user?.lastName].filter(Boolean).join(" ") ||
  review.user?.userName ||
  "Unknown user";

const identityName = (identity?: Record<string, any> | null) =>
  [identity?.firstName, identity?.middleName, identity?.lastName]
    .filter(Boolean)
    .join(" ") || "Not available";

const DetailField = ({ label, value }: { label: string; value?: any }) => (
  <div className="p-4 border border-gray-100 rounded-xl bg-gray-50">
    <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
      {label}
    </p>
    <p className="mt-1 text-sm font-medium text-gray-900 break-words">
      {value || "Not available"}
    </p>
  </div>
);

const ApprovalLabel = ({
  status,
  source,
  manuallyApproved,
}: {
  status?: string | null;
  source?: AddressReview["approvalSource"];
  manuallyApproved: boolean;
}) =>
  status === "verified" ? (
    <span className="inline-block px-3 py-1 text-xs font-semibold text-gray-700 bg-gray-100 rounded-full">
      {source === "manual" || manuallyApproved
        ? "Manually approved"
        : source === "automatic"
          ? "Automatically approved"
          : "Approval source not recorded"}
    </span>
  ) : null;

const IdentityImage = ({
  label,
  url,
}: {
  label: string;
  url?: string | null;
}) => {
  const [failed, setFailed] = useState(false);
  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-gray-900">{label}</p>
      {url && !failed ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open ${label}`}
        >
          <img
            src={url}
            alt={label}
            onError={() => setFailed(true)}
            className="object-contain w-full h-40 border border-gray-200 rounded-lg bg-gray-50"
          />
        </a>
      ) : (
        <p className="flex items-center justify-center h-40 p-3 text-xs text-center text-gray-500 border border-gray-200 rounded-lg bg-gray-50">
          {failed
            ? "Image unavailable. Open MetaMap to inspect the original."
            : "Not available for this verification."}
        </p>
      )}
    </div>
  );
};

const KycProfile = ({
  review,
  onDecision,
  loading,
  loadError,
  canDecide,
}: {
  review: AddressReview;
  onDecision: (decision: "approve" | "reject") => void;
  loading: boolean;
  loadError: boolean;
  canDecide: boolean;
}) => (
  <div className="p-6 space-y-5 border-b border-gray-100">
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      <DetailField label="First name" value={review.user?.firstName} />
      <DetailField label="Last name" value={review.user?.lastName} />
      <DetailField
        label="Username"
        value={review.user?.userName ? `@${review.user.userName}` : undefined}
      />
      <DetailField label="Email" value={review.user?.email} />
      <DetailField label="Phone number" value={review.user?.phoneNumber} />
      <DetailField label="User ID" value={review.user?.id} />
    </div>
    <section
      aria-label="Identity verification"
      className="p-4 border border-gray-200 rounded-xl"
    >
      <h3 className="mb-3 font-semibold text-gray-900">
        Identity verification
      </h3>
      <VerificationBadge label="Identity" status={review.identity.status} />
      {canDecide && !loading && (
        <div className="mt-2">
          <ApprovalLabel
            status={review.identity.status}
            source={review.identity.approvalSource}
            manuallyApproved={review.identity.decision?.status === "approved"}
          />
        </div>
      )}
      <div className="grid gap-3 mt-4 sm:grid-cols-2">
        <DetailField
          label="NIN status"
          value={
            review.identity.ninStatus
              ? verificationLabel(review.identity.ninStatus)
              : "Not recorded"
          }
        />
        <DetailField
          label="BVN status"
          value={
            review.identity.bvnStatus
              ? verificationLabel(review.identity.bvnStatus)
              : "Not recorded"
          }
        />
      </div>
      {loading ? (
        <p role="status" className="mt-4 text-sm text-gray-500">
          {loadError
            ? "Identity evidence unavailable."
            : "Loading identity evidence…"}
        </p>
      ) : (
        <>
          <div className="grid gap-4 mt-4 sm:grid-cols-3">
            <IdentityImage
              key={`nin-${review.identity.ninImageUrl}`}
              label="NIN ID image"
              url={review.identity.ninImageUrl}
            />
            <IdentityImage
              key={`bvn-${review.identity.bvnImageUrl}`}
              label="BVN ID image"
              url={review.identity.bvnImageUrl}
            />
            <IdentityImage
              key={`selfie-${review.identity.selfieImageUrl}`}
              label="Captured selfie"
              url={review.identity.selfieImageUrl}
            />
          </div>
          {review.identity.evidenceUnavailable && (
            <p className="mt-3 text-sm text-amber-700">
              Some MetaMap evidence could not be refreshed. Stored evidence is
              shown.
            </p>
          )}
          {(review.identity.providerRejectionReason ||
            review.identity.failureReason) && (
            <div className="mt-4">
              <DetailField
                label="MetaMap rejection reason"
                value={
                  review.identity.providerRejectionReason ||
                  (!review.identity.decision
                    ? review.identity.failureReason
                    : null)
                }
              />
            </div>
          )}
          {review.identity.decision && (
            <div className="p-3 mt-4 border border-gray-200 rounded-lg">
              <p className="text-sm font-semibold">
                Identity {review.identity.decision.status} by @
                {review.identity.decision.actorUserName}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                {review.identity.decision.reason}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {DateTime.fromISO(review.identity.decision.decidedAt).toFormat(
                  "DD t",
                )}
              </p>
            </div>
          )}
        </>
      )}
      {review.identity.verificationId && (
        <p className="mt-3 text-xs text-gray-500 break-all">
          Verification ID: {review.identity.verificationId}
        </p>
      )}
      {review.identity.dashboardUrl && (
        <a
          href={review.identity.dashboardUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-block mt-3 text-sm font-semibold text-orange-600"
        >
          Open identity in MetaMap
        </a>
      )}
      {canDecide && !loading &&
        review.identity.status !== "verified" &&
        !review.identity.decision &&
        review.identity.canDecide && (
          <div className="mt-4">
            {review.identity.status === "rejected" && (
              <p className="mb-3 text-sm text-gray-600">
                This identity was automatically rejected. Staff can confirm the
                rejection or approve it after reviewing the evidence, if
                approval is eligible.
              </p>
            )}
            {!review.identity.canApprove && (
              <p className="mb-3 text-sm text-amber-700">
                {review.identity.approvalBlockedReason ||
                  "NIN, BVN and captured selfie evidence are required before approval."}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-3">
              <button
                onClick={() => onDecision("reject")}
                className="px-4 py-2 text-sm font-semibold border rounded-lg text-rose-700 border-rose-200"
              >
                {review.identity.status === "rejected"
                  ? "Confirm rejection"
                  : "Reject identity"}
              </button>
              <button
                disabled={!review.identity.canApprove}
                onClick={() => onDecision("approve")}
                className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-gray-950 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Approve identity
              </button>
            </div>
          </div>
        )}
    </section>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h3 className="font-semibold text-gray-900">Address verification</h3>
      <VerificationBadge label="Address" status={review.status} />
      <ApprovalLabel
        status={review.status}
        source={review.approvalSource}
        manuallyApproved={review.decision?.status === "approved"}
      />
      {review.verification.dashboardUrl && (
        <a
          href={review.verification.dashboardUrl}
          target="_blank"
          rel="noreferrer"
          className="text-sm font-semibold text-orange-600"
        >
          Open address in MetaMap
        </a>
      )}
    </div>
  </div>
);

type AddressVerificationReviewsProps = {
  kycView?: boolean;
  canDecideAddress?: boolean;
  canDecideIdentity?: boolean;
  reviewerType: "admin" | "support";
  Sidebar: React.ComponentType;
  fetchReviews: (status: string, limit: number, page: number) => Promise<any>;
  fetchEmails?: (id: number, page: number) => Promise<EmailActivityPage>;
  fetchReview?: (id: number) => Promise<{ review: AddressReview }>;
  decideIdentity?: AddressVerificationReviewsProps["decideReview"];
  decideReview: (
    id: number,
    decision: "approve" | "reject",
    reason: string,
    verificationId: string,
    reviewRevision: string,
  ) => Promise<any>;
};

const AddressVerificationReviews: React.FC<AddressVerificationReviewsProps> = ({
  reviewerType,
  kycView = false,
  canDecideAddress = true,
  canDecideIdentity = true,
  Sidebar,
  fetchReviews,
  fetchReview,
  fetchEmails,
  decideIdentity,
  decideReview,
}) => {
  const [status, setStatus] = useState<ReviewTab>(
    kycView ? "needs_review" : "pending_manual_review",
  );
  const [detailTab, setDetailTab] = useState<"verification" | "emails">(
    "verification",
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [decisionTarget, setDecisionTarget] = useState<{
    type: "identity" | "address";
    action: "approve" | "reject";
    review: AddressReview;
  } | null>(null);
  const decision = decisionTarget?.action;
  const decisionType = decisionTarget?.type ?? "address";
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");

  const query = useQuery(
    [
      kycView ? "kycReviews" : "addressVerificationReviews",
      reviewerType,
      status,
      currentPage,
    ],
    () => fetchReviews(status, PAGE_LIMIT, currentPage),
  );
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  const visibleTabs = kycView
    ? kycTabs
    : reviewerType === "admin"
      ? adminTabs
      : tabs;
  const automaticTab =
    status === "automatically_approved" || status === "automatically_rejected";
  const reviews: AddressReview[] = useMemo(
    () => query.data?.reviews ?? [],
    [query.data?.reviews],
  );
  const selectedSummary = useMemo(
    () => reviews.find((review) => review.id === selectedId) ?? reviews[0],
    [reviews, selectedId],
  );
  const detailQuery = useQuery(
    ["kycReview", reviewerType, selectedSummary?.id],
    () => fetchReview!(selectedSummary!.id),
    { enabled: kycView && !!fetchReview && !!selectedSummary, retry: false },
  );
  const detailReady =
    !fetchReview ||
    (detailQuery.data?.review?.id === selectedSummary?.id &&
      !!selectedSummary &&
      !detailQuery.isError &&
      !detailQuery.isFetching);
  const selected =
    detailQuery.data?.review?.id === selectedSummary?.id
      ? detailQuery.data?.review
      : selectedSummary;
  const detailsPane = useRef<HTMLElement>(null);
  useEffect(() => {
    if (detailsPane.current) detailsPane.current.scrollTop = 0;
  }, [selectedSummary?.id]);
  const comparison = selected?.locationComparison;
  const approvedByAddressName = comparison?.reason === "address_name_match";
  const selection = comparison?.candidateSelection;
  // Older comparisons predate the explicit flag. These reasons only occur
  // after a precise bill match; imprecise/unknown outcomes remain unconfirmed.
  const billLocationAccepted =
    comparison?.billLocationAccepted ??
    [
      "within_range",
      "out_of_range",
      "distance_uncertain",
      "location_inaccurate",
    ].includes(comparison?.reason ?? "");

  useEffect(() => {
    if (query.data && currentPage > totalPages) setCurrentPage(totalPages);
  }, [query.data, currentPage, totalPages]);

  useEffect(() => {
    if (reviews.length && !reviews.some((review) => review.id === selectedId)) {
      setSelectedId(reviews[0].id);
    }
    if (!reviews.length) setSelectedId(null);
  }, [reviews, selectedId]);

  const documentChecks = selected?.verification.checks;
  const documentChecksPassed =
    !!documentChecks &&
    [
      "stepErrorFree",
      "addressExtracted",
      "fullNameExtracted",
      "emissionDateExtracted",
      "emissionDateRecent",
      "documentNotExpired",
    ].every((check) => documentChecks[check] === true);
  const ownerMismatch =
    documentChecks?.fullNameExtracted === true &&
    documentChecks?.ownerNameMatchesIdentity === false;

  const mutation = useMutation(
    ({
      target,
      note,
    }: {
      target: NonNullable<typeof decisionTarget>;
      note: string;
    }) => {
      const identity = target.type === "identity";
      const submit = identity ? decideIdentity! : decideReview;
      return submit(
        target.review.id,
        target.action,
        note,
        identity
          ? target.review.identity.verificationId!
          : target.review.verification.id!,
        identity
          ? target.review.identity.reviewRevision!
          : target.review.reviewRevision,
      );
    },
    {
      onSuccess: (data, { target }) => {
        if (data?.emailQueued === false)
          toast.warning(
            "The decision was saved, but the email could not be queued. Please contact the operations team.",
          );
        toast.success(
          `${target.type === "identity" ? "Identity" : "Address"} verification ${target.action === "approve" ? "approved" : "rejected"}`,
        );
        setDecisionTarget(null);
        setReason("");
        setSelectedId(null);
        query.refetch();
        queryClient.invalidateQueries(["verificationEmails", target.review.id]);
        queryClient.invalidateQueries(["kycReview", reviewerType]);
        queryClient.invalidateQueries(["kycReviews", reviewerType]);
      },
    },
  );

  const changeStatus = (nextStatus: ReviewTab) => {
    setStatus(nextStatus);
    setCurrentPage(1);
    setSelectedId(null);
  };

  const openDecision = (
    nextDecision: "approve" | "reject",
    type: "identity" | "address" = "address",
  ) => {
    if (type === "identity" ? !canDecideIdentity : !canDecideAddress) return;
    setReason("");
    const binding =
      type === "identity"
        ? selected?.identity
        : {
            verificationId: selected?.verification.id,
            reviewRevision: selected?.reviewRevision,
          };
    if (
      !selected ||
      !detailReady ||
      !binding?.verificationId ||
      !binding.reviewRevision
    ) {
      toast.error(`Reload the ${type} review before submitting a decision.`);
      return;
    }
    setDecisionTarget({ action: nextDecision, type, review: selected });
  };

  const canSubmit =
    (decision === "approve" && decisionType === "address") ||
    reason.trim().length >= 3;

  return (
    <Container
      className={kycView ? "kyc-workspace flex-col md:flex-row" : undefined}
    >
      <Sidebar />
      <Main
        className={`min-w-0 mx-3 mt-3 text-left md:mx-6 ${kycView ? "kyc-main" : ""}`}
      >
        <div className="pb-6 border-b border-gray-200 kyc-header">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="text-sm font-semibold tracking-wide text-orange-600 uppercase">
                Compliance review
              </p>
              <h1 className="mt-1 text-3xl font-bold text-gray-950">
                {kycView ? "KYC" : "Address verification"}
              </h1>
              <p className="max-w-2xl mt-2 text-sm leading-6 text-gray-600">
                {kycView ? (
                  "Review each user’s identity and address verification together. Select a user to inspect the evidence and review each verification separately."
                ) : (
                  <>
                    Resolve valid proof-of-residency documents where the bill
                    owner differs from the verified account holder, such as a
                    landlord or family member.
                  </>
                )}
              </p>
            </div>
            <button
              onClick={() => {
                query.refetch();
                if (fetchReview && selectedSummary) detailQuery.refetch();
              }}
              className="self-start px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50"
            >
              <i className="mr-2 fa fa-rotate-right" /> Refresh
            </button>
          </div>
          {kycView && (
            <p className="mt-3 text-xs text-gray-500">
              Needs review shows only address verifications awaiting manual
              review, with a submitted document and confirmed home location.
            </p>
          )}

          <div className="flex gap-2 mt-6 overflow-x-auto">
            {visibleTabs.map((tab) => (
              <button
                key={tab.value}
                aria-pressed={status === tab.value}
                onClick={() => changeStatus(tab.value)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition ${
                  status === tab.value
                    ? "bg-gray-950 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="kyc-panes grid gap-6 py-6 xl:grid-cols-[360px_minmax(0,1fr)]">
          <section
            aria-label="Verification list"
            className="kyc-list"
            tabIndex={0}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-900">
                {total}{" "}
                {kycView
                  ? total === 1
                    ? "user"
                    : "users"
                  : statusLabel[status].toLowerCase()}
              </h2>
              {query.isFetching && (
                <span className="text-xs text-gray-500">Updating…</span>
              )}
            </div>

            <div className="space-y-3">
              {query.isLoading && (
                <p className="py-8 text-sm text-gray-500" role="status">
                  Loading verifications…
                </p>
              )}
              {query.isError && (
                <p role="alert" className="p-4 text-sm text-rose-700">
                  Unable to load {kycView ? "KYC users" : "address reviews"}.
                  Please refresh to try again.
                </p>
              )}
              {!query.isLoading && !query.isError && reviews.length === 0 && (
                <div className="px-6 text-center bg-white border border-gray-200 py-14 rounded-2xl">
                  <div className="flex items-center justify-center w-12 h-12 mx-auto rounded-full text-emerald-700 bg-emerald-50">
                    <i className="fa fa-check" />
                  </div>
                  <p className="mt-4 font-semibold text-gray-900">
                    {kycView
                      ? "No users found"
                      : status === "pending_manual_review"
                        ? "Nothing waiting here"
                        : "No verifications found"}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {kycView
                      ? "No users match this verification filter."
                      : status === "pending_manual_review"
                        ? "New verifications needing manual review will appear here."
                        : `There are no ${statusLabel[status].toLowerCase()} verifications to show.`}
                  </p>
                </div>
              )}
              {reviews.map((review) => (
                <button
                  key={review.id}
                  onClick={() => setSelectedId(review.id)}
                  className={`w-full rounded-2xl border p-4 text-left transition ${
                    selected?.id === review.id
                      ? "border-orange-400 bg-orange-50 shadow-sm"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold truncate text-gray-950">
                        {displayName(review)}
                      </p>
                      <p className="mt-1 text-xs text-gray-500 truncate">
                        @{review.user?.userName || "unknown"} · User #
                        {review.user?.id}
                      </p>
                    </div>
                    {!kycView && (
                      <span
                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusStyle[review.status as ReviewStatus]}`}
                      >
                        {
                          statusLabel[
                            automaticTab
                              ? status
                              : (review.status as ReviewStatus)
                          ]
                        }
                      </span>
                    )}
                  </div>
                  <div className="pt-3 mt-4 border-t border-gray-200/70">
                    {kycView ? (
                      <div className="flex flex-wrap gap-2">
                        <VerificationBadge
                          label="Identity"
                          status={review.identity.status}
                        />
                        <VerificationBadge
                          label="Address"
                          status={review.status}
                        />
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-gray-500">Bill owner</p>
                        <p className="mt-1 text-sm font-medium text-gray-800 truncate">
                          {review.document.ownerName || "Not extracted"}
                        </p>
                      </>
                    )}
                    <p className="mt-2 text-xs text-gray-500">
                      {kycView && "Updated "}
                      {(kycView ? review.updatedAt : review.submittedAt)
                        ? DateTime.fromISO(
                            (kycView ? review.updatedAt : review.submittedAt)!,
                          ).toFormat("DD t")
                        : "Date unavailable"}
                    </p>
                  </div>
                </button>
              ))}
            </div>
            {total > 0 && (
              <nav
                aria-label="Verification pagination"
                className="mt-5 space-y-3 text-sm"
              >
                <p className="text-gray-500">
                  Showing {(currentPage - 1) * PAGE_LIMIT + 1}–
                  {Math.min(currentPage * PAGE_LIMIT, total)} of {total}
                </p>
                <div className="flex items-center justify-between gap-2">
                  <button
                    disabled={currentPage <= 1 || query.isFetching}
                    onClick={() => {
                      setSelectedId(null);
                      setCurrentPage((page) => page - 1);
                    }}
                    className="px-3 py-2 font-semibold border border-gray-200 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    disabled={currentPage >= totalPages || query.isFetching}
                    onClick={() => {
                      setSelectedId(null);
                      setCurrentPage((page) => page + 1);
                    }}
                    className="px-3 py-2 font-semibold border border-gray-200 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </nav>
            )}
          </section>

          <section
            aria-label="Verification details"
            className="min-w-0 kyc-details"
            ref={detailsPane}
            tabIndex={0}
          >
            {!selected ? (
              <div className="flex items-center justify-center min-h-[420px] bg-gray-50 border border-dashed border-gray-300 rounded-2xl">
                <p className="text-sm text-gray-500">
                  {kycView
                    ? "Select a user to inspect their KYC."
                    : "Select a review to inspect it."}
                </p>
              </div>
            ) : (
              <div className="overflow-hidden bg-white border border-gray-200 shadow-sm rounded-2xl">
                <div className="flex flex-col justify-between gap-4 p-6 border-b border-gray-100 md:flex-row md:items-center">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-xl font-bold text-gray-950">
                        {displayName(selected)}
                      </h2>
                      {!kycView && (
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${statusStyle[selected.status as ReviewStatus]}`}
                        >
                          {
                            statusLabel[
                              automaticTab
                                ? status
                                : (selected.status as ReviewStatus)
                            ]
                          }
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      {selected.user?.email} · Level {selected.user?.level}
                    </p>
                  </div>
                  {!kycView && selected.verification.dashboardUrl && (
                    <a
                      href={selected.verification.dashboardUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-semibold text-orange-600 hover:text-orange-700"
                    >
                      Open in MetaMap{" "}
                      <i className="ml-1 fa fa-arrow-up-right-from-square" />
                    </a>
                  )}
                </div>

                {kycView && fetchEmails && (
                  <div
                    className="flex gap-2 px-6 pt-4"
                    aria-label="User details"
                  >
                    <button
                      aria-pressed={detailTab === "verification"}
                      onClick={() => setDetailTab("verification")}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold ${detailTab === "verification" ? "bg-gray-950 text-white" : "bg-gray-100"}`}
                    >
                      Verification
                    </button>
                    <button
                      aria-pressed={detailTab === "emails"}
                      onClick={() => setDetailTab("emails")}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold ${detailTab === "emails" ? "bg-gray-950 text-white" : "bg-gray-100"}`}
                    >
                      Email activity
                    </button>
                  </div>
                )}
                {kycView && detailTab === "emails" && fetchEmails ? (
                  <VerificationEmailActivity
                    key={selected.id}
                    reviewId={selected.id}
                    fetchEmails={fetchEmails}
                  />
                ) : (
                  <>
                    {kycView && (
                      <>
                        {detailQuery.isError && (
                          <p role="alert" className="p-6 text-sm text-rose-700">
                            Unable to load the latest KYC evidence. Refresh
                            before making a decision.
                          </p>
                        )}
                        <KycProfile
                          review={selected}
                          canDecide={canDecideIdentity}
                          loading={!detailReady}
                          loadError={detailQuery.isError}
                          onDecision={(action) =>
                            openDecision(action, "identity")
                          }
                        />
                      </>
                    )}

                    {kycView && !selected.status ? (
                      <p className="p-6 text-sm text-gray-500">
                        This user has not started address verification.
                      </p>
                    ) : (
                      <div className="grid gap-6 p-6 lg:grid-cols-[minmax(260px,0.8fr)_minmax(0,1.2fr)]">
                        <div>
                          <p className="mb-3 text-sm font-semibold text-gray-900">
                            Proof of residency
                          </p>
                          <div className="overflow-hidden bg-gray-100 border border-gray-200 rounded-2xl aspect-[4/3]">
                            {selected.document.imageUrl ? (
                              <a
                                href={selected.document.imageUrl}
                                target="_blank"
                                rel="noreferrer"
                                title="Open full-size document"
                              >
                                <img
                                  src={selected.document.imageUrl}
                                  alt="Uploaded proof of residency"
                                  className="object-contain w-full h-full"
                                />
                              </a>
                            ) : (
                              <div className="flex items-center justify-center h-full text-sm text-gray-500">
                                Document preview unavailable
                              </div>
                            )}
                          </div>
                          <p className="mt-2 text-xs text-gray-500">
                            Click the preview to inspect the original document.
                          </p>
                        </div>

                        <div className="space-y-6">
                          {(status === "automatically_rejected" ||
                            (kycView &&
                              selected.status === "rejected" &&
                              !selected.decision)) && (
                            <DetailField
                              label="Rejection reason"
                              value={
                                selected.verification.rejectionReason ||
                                "No rejection reason was recorded for this verification."
                              }
                            />
                          )}
                          <DetailField
                            label="Address entered by user"
                            value={
                              selected.submittedAddress ||
                              "Not captured for this verification"
                            }
                          />
                          {(selected.location ||
                            selected.locationComparison) && (
                            <div>
                              <p className="mb-3 text-sm font-semibold text-gray-900">
                                Home location evidence
                              </p>
                              {selected.location && (
                                <div className="p-4 mb-3 border border-gray-100 rounded-xl bg-gray-50">
                                  <p className="text-xs font-semibold text-gray-500 uppercase">
                                    Address shown during live-location
                                    confirmation
                                  </p>
                                  <p className="mt-1 text-sm font-medium text-gray-900 break-words">
                                    {selected.location.resolvedAddress ||
                                      "Not recorded for this capture"}
                                  </p>
                                  {selected.location.addressSource ===
                                    "google_places_nearby_agreement" && (
                                    <p className="mt-2 text-xs text-gray-600">
                                      Estate suggestion from agreement between
                                      nearby addresses; not a verified property
                                      boundary.
                                    </p>
                                  )}
                                  {selected.location.resolvedAddress && (
                                    <p className="mt-2 text-xs text-[#5E5E5E]">
                                      Google Maps
                                    </p>
                                  )}
                                </div>
                              )}
                              <div className="grid gap-3 sm:grid-cols-2">
                                <DetailField
                                  label="GPS accuracy"
                                  value={
                                    selected.location
                                      ? `${selected.location.accuracy.toFixed(1)} m`
                                      : "Not available"
                                  }
                                />
                                <DetailField
                                  label="Location captured"
                                  value={selected.location?.capturedAt}
                                />
                                <DetailField
                                  label="Location confirmed"
                                  value={selected.location?.confirmedAt}
                                />
                                <DetailField
                                  label={
                                    approvedByAddressName
                                      ? "Distance check"
                                      : billLocationAccepted
                                        ? "Distance from matched bill address"
                                        : "Distance to unconfirmed Google candidate"
                                  }
                                  value={
                                    approvedByAddressName
                                      ? "Not needed — address names matched"
                                      : selected.locationComparison
                                            ?.distanceMeters != null
                                        ? `${selected.locationComparison.distanceMeters} m`
                                        : "Not calculated"
                                  }
                                />
                                <DetailField
                                  label="Allowed distance"
                                  value={
                                    selected.locationComparison
                                      ? `${selected.locationComparison.maxDistanceMeters} m`
                                      : "Not evaluated"
                                  }
                                />
                                <DetailField
                                  label="Bill geocoding precision"
                                  value={
                                    selected.locationComparison?.precision ||
                                    "Not evaluated"
                                  }
                                />
                                <DetailField
                                  label="Location result"
                                  value={
                                    selected.locationComparison?.reason.replace(
                                      /_/g,
                                      " ",
                                    ) || "Existing identity checks used"
                                  }
                                />
                                <DetailField
                                  label="Comparison checked"
                                  value={comparison?.checkedAt}
                                />
                                <DetailField
                                  label="Google place ID used for comparison"
                                  value={comparison?.billPlaceId}
                                />
                              </div>
                              {comparison?.textMatch && (
                                <div className="p-3 mt-3 text-sm text-gray-700 rounded-lg bg-gray-50">
                                  <p>
                                    {approvedByAddressName
                                      ? "Approved by a shared address name with no known state conflict. An exact property match was not required."
                                      : comparison.textMatch.reason ===
                                          "state_conflict"
                                        ? "State evidence conflicts or is ambiguous; address-name approval was not used. The distance check was considered instead."
                                        : "No shared address name was found. The distance check was considered instead."}
                                  </p>
                                  <p className="mt-1">
                                    Bill state:{" "}
                                    {comparison.textMatch.billStates.join(
                                      ", ",
                                    ) || "Not identified"}
                                    . Live-location state:{" "}
                                    {comparison.textMatch.liveStates.join(
                                      ", ",
                                    ) || "Not identified"}
                                    .
                                  </p>
                                </div>
                              )}
                              {!billLocationAccepted &&
                                comparison?.distanceMeters != null && (
                                  <p className="p-3 mt-3 text-sm rounded-lg bg-amber-50 text-amber-900">
                                    This distance is to an unconfirmed
                                    candidate; it does not establish the bill’s
                                    location.
                                  </p>
                                )}
                              {comparison && !approvedByAddressName && (
                                <div className="p-4 mt-4 border border-gray-200 rounded-xl">
                                  <p className="text-sm font-semibold text-gray-900">
                                    Google candidate assessment
                                  </p>
                                  {selection ? (
                                    <>
                                      <p className="mt-2 text-sm text-gray-700">
                                        {selection.candidateCount} candidates
                                        returned;{" "}
                                        {selection.matchingCandidateCount}{" "}
                                        supported properties.
                                      </p>
                                      <p className="mt-1 text-sm text-gray-700">
                                        {candidateReasons[selection.reason] ||
                                          "Selection details unavailable"}
                                      </p>
                                      {selection.candidates?.length ? (
                                        <details className="mt-3">
                                          <summary className="text-sm font-medium text-gray-900 cursor-pointer">
                                            Inspect candidate checks
                                          </summary>
                                          <div className="mt-3 space-y-3">
                                            {selection.candidates.map(
                                              (candidate) => (
                                                <div
                                                  key={candidate.index}
                                                  className="p-3 text-sm rounded-lg bg-gray-50"
                                                >
                                                  <p className="font-semibold text-gray-900">
                                                    Candidate {candidate.index}
                                                    {selection.referenceCandidateIndex ===
                                                    candidate.index
                                                      ? " · Used for displayed distance"
                                                      : ""}
                                                  </p>
                                                  <p className="mt-1 text-gray-700">
                                                    {candidateReasons[
                                                      candidate.reason
                                                    ] ||
                                                      "Assessment unavailable"}
                                                  </p>
                                                  <p className="mt-1 text-xs text-gray-600 break-all">
                                                    Place ID:{" "}
                                                    {candidate.placeId ||
                                                      "Not available"}
                                                  </p>
                                                  <p className="mt-1 text-xs text-gray-600">
                                                    Precision:{" "}
                                                    {candidate.precision ||
                                                      "Unknown"}{" "}
                                                    · Google partial match:{" "}
                                                    {candidate.partialMatch
                                                      ? "Yes"
                                                      : "No"}
                                                  </p>
                                                  <dl className="grid gap-1 mt-2 sm:grid-cols-2">
                                                    {Object.entries(
                                                      candidate.checks,
                                                    ).map(([key, passed]) => (
                                                      <div key={key}>
                                                        <dt className="inline text-gray-600">
                                                          {candidateChecks[
                                                            key
                                                          ] || key}
                                                          :{" "}
                                                        </dt>
                                                        <dd className="inline font-medium">
                                                          {passed === true
                                                            ? "Passed"
                                                            : passed === false
                                                              ? "Not satisfied"
                                                              : "Not assessed"}
                                                        </dd>
                                                      </div>
                                                    ))}
                                                  </dl>
                                                </div>
                                              ),
                                            )}
                                          </div>
                                        </details>
                                      ) : (
                                        <p className="mt-2 text-xs text-gray-500">
                                          Per-candidate checks were not recorded
                                          for this comparison.
                                        </p>
                                      )}
                                    </>
                                  ) : (
                                    <p className="mt-2 text-sm text-gray-500">
                                      Candidate assessment was not recorded for
                                      this comparison.
                                    </p>
                                  )}
                                </div>
                              )}
                              <p className="mt-2 text-xs text-gray-500">
                                Distance uses Google Maps geocoding. Approximate
                                or out-of-range results require review.
                              </p>
                            </div>
                          )}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <p className="text-sm font-semibold text-gray-900">
                                Extracted document details
                              </p>
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded-md ${
                                  documentChecksPassed
                                    ? "text-emerald-700 bg-emerald-50"
                                    : "text-amber-800 bg-amber-50"
                                }`}
                              >
                                {documentChecksPassed
                                  ? "Document checks passed"
                                  : "Document checks need review"}
                              </span>
                            </div>
                            <div className="grid gap-3 sm:grid-cols-2">
                              <DetailField
                                label="Bill owner"
                                value={selected.document.ownerName}
                              />
                              <DetailField
                                label="Emission date"
                                value={selected.document.emissionDate}
                              />
                              <div className="sm:col-span-2">
                                <DetailField
                                  label="Address extracted from bill"
                                  value={selected.document.address}
                                />
                              </div>
                            </div>
                          </div>

                          <div>
                            <p className="mb-3 text-sm font-semibold text-gray-900">
                              Verified identity comparison
                            </p>
                            <div className="grid gap-3 sm:grid-cols-2">
                              <DetailField
                                label={`NIN ${selected.identity.ninNumber || ""}`}
                                value={identityName(selected.identity.nin)}
                              />
                              <DetailField
                                label={`BVN ${selected.identity.bvnNumber || ""}`}
                                value={identityName(selected.identity.bvn)}
                              />
                            </div>
                            {selected.status === "pending_manual_review" && (
                              <div className="flex gap-3 p-4 mt-3 text-sm border text-amber-900 border-amber-200 rounded-xl bg-amber-50">
                                <i className="mt-1 fa fa-triangle-exclamation" />
                                <p>
                                  {ownerMismatch
                                    ? "The document owner name does not match the verified identity. Review the name difference and document evidence before deciding."
                                    : "Review the provider outcome and available document evidence before deciding. Incomplete or failed checks must not be treated as passed."}
                                </p>
                              </div>
                            )}
                          </div>

                          {selected.verification.checks && (
                            <div>
                              <p className="mb-3 text-sm font-semibold text-gray-900">
                                Automated checks
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(
                                  selected.verification.checks,
                                ).map(([check, passed]) => (
                                  <span
                                    key={check}
                                    className={`rounded-full px-3 py-1.5 text-xs font-medium ${
                                      passed
                                        ? "bg-emerald-50 text-emerald-700"
                                        : "bg-amber-50 text-amber-700"
                                    }`}
                                  >
                                    <i
                                      className={`fa mr-1.5 ${passed ? "fa-check" : "fa-minus"}`}
                                    />
                                    {check
                                      .replace(/([A-Z])/g, " $1")
                                      .toLowerCase()}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {selected.decision && (
                            <div className="p-4 border border-gray-200 rounded-xl">
                              <p className="text-sm font-semibold text-gray-900">
                                Decision record
                              </p>
                              <p className="mt-2 text-sm text-gray-600">
                                {selected.decision.reason || "No note supplied"}
                              </p>
                              <p className="mt-2 text-xs text-gray-500">
                                By @
                                {selected.decision.actorUserName ||
                                  selected.decision.adminUserName ||
                                  "reviewer"}{" "}
                                (
                                {selected.decision.actorType === "support"
                                  ? "Support"
                                  : "Admin"}
                                )
                                {selected.decision.decidedAt
                                  ? ` · ${DateTime.fromISO(selected.decision.decidedAt).toFormat("DD t")}`
                                  : ""}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {canDecideAddress && selected.canDecideAddress && detailReady && (
                      <div className="flex flex-col justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 sm:flex-row">
                        <button
                          onClick={() => openDecision("reject")}
                          className="px-5 py-2.5 text-sm font-semibold text-rose-700 bg-white border border-rose-200 rounded-xl hover:bg-rose-50"
                        >
                          {kycView ? "Reject address" : "Reject verification"}
                        </button>
                        <button
                          onClick={() => openDecision("approve")}
                          disabled={
                            kycView && selected.identity.status !== "verified"
                          }
                          title={
                            kycView && selected.identity.status !== "verified"
                              ? "Verify identity before approving the address"
                              : undefined
                          }
                          className="px-5 py-2.5 text-sm font-semibold text-white bg-gray-950 rounded-xl hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {kycView
                            ? "Approve address"
                            : "Approve verified Level 2"}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </section>
        </div>

        <ReactModal
          isOpen={!!decision}
          onRequestClose={() => !mutation.isLoading && setDecisionTarget(null)}
          ariaHideApp={false}
          contentLabel={`${decisionType === "identity" ? "Identity" : "Address"} verification decision`}
          className="w-[calc(100%-2rem)] max-w-lg p-0 overflow-hidden bg-white shadow-2xl outline-none rounded-2xl"
          overlayClassName="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
        >
          <div className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold tracking-wide text-orange-600 uppercase">
                  Manual decision
                </p>
                <h3 className="mt-1 text-xl font-bold text-gray-950">
                  {decision === "approve" ? "Approve" : "Reject"} {decisionType}
                </h3>
              </div>
              <button
                onClick={() => !mutation.isLoading && setDecisionTarget(null)}
                className="text-gray-400 hover:text-gray-700"
                aria-label="Close"
              >
                <i className="fa fa-xmark" />
              </button>
            </div>
            <p className="mt-4 text-sm leading-6 text-gray-600">
              {decision === "approve"
                ? `Confirm that ${decisionTarget ? displayName(decisionTarget.review) : "this user"} has provided sufficient evidence for this ${decisionType}.`
                : decisionType === "identity"
                  ? "Enter the rejection reason and what the user needs to correct. This text will be included in their rejection email."
                  : "Explain why this document cannot be accepted. The user will be asked to try again."}
            </p>
            <label className="block mt-5 text-sm font-semibold text-gray-800">
              {decisionType === "identity"
                ? decision === "reject"
                  ? "Rejection reason and required action"
                  : "Review reason"
                : decision === "approve"
                  ? "Review note (optional)"
                  : "Rejection reason"}
              <textarea
                value={reason}
                onChange={(event) =>
                  setReason(event.target.value.slice(0, 500))
                }
                rows={4}
                placeholder={
                  decisionType === "identity"
                    ? "Explain your identity verification decision"
                    : decision === "approve"
                      ? "e.g. Tenant confirmed; landlord utility bill supported by tenancy evidence"
                      : "Tell the user what needs to be corrected"
                }
                className="w-full p-3 mt-2 text-sm border-gray-300 rounded-xl focus:border-orange-400 focus:ring-orange-400"
              />
            </label>
          </div>
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={() => !mutation.isLoading && setDecisionTarget(null)}
              className="px-4 py-2 text-sm font-semibold text-gray-600"
            >
              Cancel
            </button>
            <button
              disabled={!canSubmit || mutation.isLoading}
              onClick={() =>
                decisionTarget &&
                mutation.mutate({ target: decisionTarget, note: reason.trim() })
              }
              className={`px-5 py-2.5 text-sm font-semibold text-white rounded-xl ${
                !canSubmit || mutation.isLoading
                  ? "cursor-not-allowed bg-gray-300"
                  : decision === "approve"
                    ? "bg-gray-950 hover:bg-black"
                    : "bg-rose-600 hover:bg-rose-700"
              }`}
            >
              {mutation.isLoading
                ? "Saving…"
                : decision === "approve"
                  ? "Confirm approval"
                  : "Confirm rejection"}
            </button>
          </div>
        </ReactModal>
      </Main>
    </Container>
  );
};

export default AddressVerificationReviews;
